import React, { forwardRef, useImperativeHandle } from 'react'
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_KEY, MY_PGA_COM_HOST } from 'env'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { LinearProgress } from '@mui/material'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import useQueryString from 'lib/hooks/use-query-string'
import { FRISCO_COACHING_CENTER_SLUG } from 'utils/constants'

const stripePromise = loadStripe(STRIPE_KEY)

// Inner component that uses Stripe hooks - must be inside Elements provider with clientSecret
const PaymentElementForm = forwardRef(({ clientSecret }, ref) => {
  const stripe = useStripe()
  const elements = useElements()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { studentId } = useQueryString()

  const paymentElementOptions = {
    layout: 'tabs',
  }

  const validatePayment = async () => {
    if (!stripe || !elements || !clientSecret) {
      return false
    }

    const { error: submitError } = await elements.submit()
    if (submitError) {
      snackbarError(submitError.message)
      return false
    }

    return true
  }

  const submitPayment = async () => {
    if (!stripe || !elements || !clientSecret) {
      return
    }

    const billingDetails = {
      // We can add more billing details here as needed
    }

    const { error: submitError } = await elements.submit()

    if (submitError) {
      snackbarError(submitError.message)
      return
    }

    const returnUrl = `${MY_PGA_COM_HOST}/${FRISCO_COACHING_CENTER_SLUG}/${studentId}/purchase/practice-session`

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'always',
      confirmParams: {
        expand: ['payment_method'],
        return_url: returnUrl,
        payment_method_data: {
          billing_details: billingDetails,
        },
      },
      clientSecret: clientSecret,
    })

    if (error) {
      snackbarError(error.message)
    }
  }

  useImperativeHandle(ref, () => ({
    submitPayment,
    validatePayment,
  }))

  return <PaymentElement id="payment-element" options={paymentElementOptions} />
})

// Outer component that provides Elements context with clientSecret
const StripePaymentElementsForm = forwardRef(
  ({ clientSecret, secretLoading }, ref) => {
    return (
      <>
        <Box mb={3} mt={4} pt={2}>
          <Typography variant="h4">Enter payment details</Typography>
        </Box>
        <Box mb={4}>
          <Typography variant="body">
            Your credit card information is not being stored in our system.
          </Typography>
        </Box>
        {secretLoading && <LinearProgress />}
        {clientSecret && (
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <PaymentElementForm ref={ref} clientSecret={clientSecret} />
          </Elements>
        )}
      </>
    )
  },
)

export default StripePaymentElementsForm
